import { HStack, Menu, Portal, Spacer, Text } from '@chakra-ui/react'
import type { FC } from 'react'
import { memo } from 'react'
import {
  BiBuildings,
  BiGroup,
  BiCreditCard,
  BiUser,
  BiExit,
  BiSun,
  BiMoon,
  BiExtension,
  BiTestTube,
  BiCalendarAlt,
  BiBookOpen,
  BiHelpCircle
} from 'react-icons/bi'
import { FaSlack } from 'react-icons/fa'
import { LuChevronRight } from 'react-icons/lu'
import { RiAdminLine } from 'react-icons/ri'

import SidebarMenuItem from './sidebarMenuItem'
import UserButton, { type UserButtonProps } from './userButton'

import { useColorMode } from '@app/components/ui/color-mode'
import { Switch } from '@app/components/ui/switch'
import Can from '@app/shared/authorization/can'
import { abilitiesFor } from '@app/shared/authorization/caslProvider'
import ICON_MAP from '@app/utils/iconMap'
import { Routes } from '@app/utils/routeHelpers'

type Props = UserButtonProps

const SidebarMenu: FC<Props> = ({ user }) => {
  const { colorMode, toggleColorMode } = useColorMode()
  const isDarkMode = colorMode === 'dark'

  const abilities = abilitiesFor(user)
  const canUpdateOrganization = abilities.can('update', 'organization')
  const canUpdateAccount = abilities.can('update', 'account')
  const showSettingsGroup = canUpdateOrganization || canUpdateAccount

  return (
    <Menu.Root>
      <Menu.Trigger>
        <UserButton user={user} />
      </Menu.Trigger>
      <Portal>
        <Menu.Positioner>
          <Menu.Content px="3" py="4" color="muted" shadow="lg" zIndex="popover">
            {showSettingsGroup && (
              <Menu.Root positioning={{ placement: 'right-start', gutter: 2 }}>
                <Menu.TriggerItem>
                  Settings <LuChevronRight />
                </Menu.TriggerItem>
                <Portal>
                  <Menu.Positioner>
                    <Menu.Content px="3" py="4" color="muted" shadow="lg" zIndex="popover">
                      <Can I="update" an="organization">
                        <SidebarMenuItem
                          icon={<BiBuildings />}
                          href="/settings/organization/edit"
                          title="Organization"
                        />
                      </Can>
                      <Can I="update" an="account">
                        <SidebarMenuItem icon={<BiUser />} href="/settings/user/edit" title="Profile" />
                      </Can>
                    </Menu.Content>
                  </Menu.Positioner>
                </Portal>
              </Menu.Root>
            )}
            <Menu.Root>
              <Menu.TriggerItem>
                Libraries <LuChevronRight />
              </Menu.TriggerItem>
              <Portal>
                <Menu.Positioner>
                  <Menu.Content px="3" py="4" color="muted" shadow="lg" zIndex="popover">
                    <SidebarMenuItem icon={<ICON_MAP.Metric />} href="/metrics" title="Metrics" />
                    <SidebarMenuItem icon={<ICON_MAP.Work />} href="/work" title="Work" />
                    <SidebarMenuItem icon={<ICON_MAP.BasicCard />} href="/cards" title="Cards" />
                  </Menu.Content>
                </Menu.Positioner>
              </Portal>
            </Menu.Root>
            <Menu.Root>
              <Menu.TriggerItem>
                Help <LuChevronRight />
              </Menu.TriggerItem>
              <Portal>
                <Menu.Positioner>
                  <Menu.Content px="3" py="4" color="muted" shadow="lg" zIndex="popover">
                    <SidebarMenuItem
                      icon={<BiCalendarAlt />}
                      href={Routes.chat}
                      title="Schedule a chat"
                      isExternal
                      target="_blank"
                    />
                    <SidebarMenuItem
                      icon={<FaSlack />}
                      href={Routes.slackCommunity}
                      title="Slack community"
                      isExternal
                      target="_blank"
                    />
                    <SidebarMenuItem
                      icon={<BiBookOpen />}
                      href={Routes.docs}
                      title="Documentation"
                      isExternal
                      target="_blank"
                    />
                    <Menu.Item id="intercom-launcher" value="chat">
                      <BiHelpCircle /> Chat
                    </Menu.Item>
                  </Menu.Content>
                </Menu.Positioner>
              </Portal>
            </Menu.Root>
            <Menu.Separator />
            <Can I="view" an="integration">
              <SidebarMenuItem icon={<BiExtension />} href="/settings/integrations" title="Integrations" />
            </Can>
            <Can I="update" an="organization">
              <SidebarMenuItem icon={<BiGroup />} href="/settings/organization/accounts" title="Team Members" />
            </Can>
            <Can I="update" a="payment">
              <SidebarMenuItem icon={<BiCreditCard />} href="/settings/billing" title="Billing" />
            </Can>
            <Can I="view" a="metricSource">
              <SidebarMenuItem title="Import status" href="/tasks" icon={<ICON_MAP.Task />} />
            </Can>
            <Can I="view" an="admin">
              <>
                <SidebarMenuItem icon={<BiTestTube />} href="/evals" title="Evals" />
                <SidebarMenuItem icon={<RiAdminLine />} href="/admin" title="Admin" isExternal target="_blank" />
              </>
            </Can>
            <Menu.Item value="dark_mode_toggle" closeOnSelect={false} onClick={toggleColorMode} rounded="md">
              <HStack>
                {isDarkMode ? <BiMoon /> : <BiSun />}
                <Text>Dark Mode</Text>
                <Spacer />
                <Switch colorPalette="gray" id="dark-mode" checked={isDarkMode} />
              </HStack>
            </Menu.Item>
            <SidebarMenuItem icon={<BiExit />} href="/sign_out" title="Logout" isExternal />
          </Menu.Content>
        </Menu.Positioner>
      </Portal>
    </Menu.Root>
  )
}

export default memo(SidebarMenu)
